/**
 * The document lifecycle as a pure reducer (§13, §23).
 *
 * `reduce(session, command) → { session, effects }` performs no I/O. Every
 * side effect the platform must carry out is returned as data, which buys two
 * things that matter for a product whose core promise is "you will not lose
 * your file":
 *
 * 1. The rules are testable exhaustively, without a filesystem. "Saving failed,
 *    is the working copy still there?" is a unit test, not a manual experiment.
 * 2. The ordering is explicit. A recovery point is *written before* a risky
 *    operation because the reducer emits it first, not because some call site
 *    remembered to.
 */
import type {
  DocumentSession, OriginalRef, RecoveryPoint, SaveFailureReason, SavedRef, WorkingCopyRef,
} from './types.js';
import { isDirty, needsRecoveryPoint } from './types.js';

/* ------------------------------------------------------------------ *
 * commands and effects
 * ------------------------------------------------------------------ */

export type Command =
  /** The user chose Edit (§4.8 — never automatic). */
  | { readonly kind: 'request-edit' }
  | { readonly kind: 'working-copy-ready'; readonly ref: WorkingCopyRef }
  | { readonly kind: 'working-copy-failed'; readonly reason: SaveFailureReason }
  /** One user edit was applied to the in-memory document. */
  | { readonly kind: 'edit-applied' }
  /** Editing paused long enough to warrant a snapshot (§23). */
  | { readonly kind: 'idle-timeout'; readonly atMs: number }
  /** App went to the background / a call arrived / the window is closing (§23). */
  | { readonly kind: 'lifecycle-suspend'; readonly atMs: number; readonly cause: SuspendCause }
  | { readonly kind: 'recovery-point-written'; readonly point: RecoveryPoint }
  | { readonly kind: 'save-as'; readonly suggestedName: string; readonly atMs: number }
  | { readonly kind: 'overwrite-original'; readonly atMs: number }
  /** The user confirmed the §13 step-8 overwrite prompt. */
  | { readonly kind: 'overwrite-confirmed'; readonly atMs: number }
  /** The destination name already exists; the user confirmed replacing it. */
  | { readonly kind: 'replace-existing-confirmed'; readonly atMs: number }
  | { readonly kind: 'save-succeeded'; readonly ref: SavedRef }
  | { readonly kind: 'save-failed'; readonly reason: SaveFailureReason }
  | { readonly kind: 'retry-save'; readonly atMs: number }
  | { readonly kind: 'discard-edits' }
  | { readonly kind: 'close' };

export type SuspendCause = 'background' | 'window-closing' | 'interruption' | 'low-memory';

export type Effect =
  | { readonly kind: 'create-working-copy'; readonly from: OriginalRef }
  /** Flush the in-memory document to the working copy (§13 step 6). */
  | { readonly kind: 'flush-working-copy'; readonly revision: number }
  | { readonly kind: 'write-recovery-point'; readonly revision: number; readonly atMs: number }
  | { readonly kind: 'prune-recovery-points'; readonly drop: readonly RecoveryPoint[] }
  /** Ask before replacing the original (§13 step 8, §4.10). */
  | { readonly kind: 'confirm-overwrite' }
  /** Ask before replacing a different existing file (§13 "never silently"). */
  | { readonly kind: 'confirm-replace-existing'; readonly name: string }
  /** Tell the user what this format cannot keep, before writing (§13, §16, §17). */
  | { readonly kind: 'warn-loss'; readonly items: readonly string[] }
  | { readonly kind: 'write-new-file'; readonly suggestedName: string; readonly revision: number }
  | { readonly kind: 'write-over-original'; readonly revision: number }
  /** Reopen what was written and check its structure (§13, §42-27). */
  | { readonly kind: 'validate-export'; readonly handle: string }
  | { readonly kind: 'schedule-retry'; readonly delayMs: number; readonly attempt: number }
  /** Surface a §34 message. Always a key, never a literal (§25). */
  | { readonly kind: 'notify'; readonly messageKey: string }
  | { readonly kind: 'release-working-copy'; readonly ref: WorkingCopyRef }
  | { readonly kind: 'keep-working-copy'; readonly ref: WorkingCopyRef; readonly reason: string };

export interface Transition {
  readonly session: DocumentSession;
  readonly effects: readonly Effect[];
}

/* ------------------------------------------------------------------ *
 * policy knobs
 * ------------------------------------------------------------------ */

export interface RecoveryPolicy {
  /** Snapshots kept per document (§23 "limit recovery copies by quantity"). */
  readonly maxPoints: number;
  /** Age beyond which a snapshot is pruned (§23 "by age"). */
  readonly maxAgeMs: number;
}

export const DEFAULT_RECOVERY_POLICY: RecoveryPolicy = Object.freeze({
  maxPoints: 5,
  maxAgeMs: 7 * 24 * 60 * 60 * 1000,
});

/** Backoff for retrying a failed save (§23 "automatic retry after failure"). */
export const RETRY_DELAYS_MS: readonly number[] = Object.freeze([1_000, 5_000, 15_000]);

/* ------------------------------------------------------------------ *
 * construction
 * ------------------------------------------------------------------ */

export interface SessionInit {
  readonly id: string;
  readonly original: OriginalRef;
  /** From the security assessment — overrides any catalog edit support (§19). */
  readonly forceReadOnly: boolean;
  /** From the format catalog's `lossOnSave`. */
  readonly lossWarnings: readonly string[];
}

/** Every document starts read-only (§4.7). There is no other entry point. */
export function createSession(init: SessionInit): DocumentSession {
  return {
    id: init.id,
    original: init.original,
    phase: 'read-only',
    workingCopy: null,
    lastSaved: null,
    recoveryPoints: [],
    revision: 0,
    savedRevision: 0,
    recoveredRevision: 0,
    saveAttempts: 0,
    lastFailure: null,
    lossWarnings: init.lossWarnings,
    forceReadOnly: init.forceReadOnly,
  };
}

/* ------------------------------------------------------------------ *
 * reducer
 * ------------------------------------------------------------------ */

const noop = (session: DocumentSession): Transition => ({ session, effects: [] });

/** Snapshots to drop under the retention policy, given a new set of points. */
function prunable(
  points: readonly RecoveryPoint[], nowMs: number, policy: RecoveryPolicy,
): RecoveryPoint[] {
  const tooOld = points.filter((p) => nowMs - p.createdAtMs > policy.maxAgeMs);
  const fresh = points.filter((p) => !tooOld.includes(p));
  // Keep the newest `maxPoints`; the rest are surplus.
  const surplus = [...fresh]
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(policy.maxPoints);
  return [...tooOld, ...surplus];
}

export function reduce(
  session: DocumentSession,
  command: Command,
  policy: RecoveryPolicy = DEFAULT_RECOVERY_POLICY,
): Transition {
  switch (command.kind) {
    case 'request-edit': {
      if (session.forceReadOnly) {
        // Encrypted, unidentified, or otherwise not safe to rewrite (§19).
        return { session, effects: [{ kind: 'notify', messageKey: 'error.featuresUnsupported' }] };
      }
      if (session.phase !== 'read-only') return noop(session);

      const effects: Effect[] = [{ kind: 'create-working-copy', from: session.original }];
      // §13: tell the user what this format can lose *before* they invest work
      // in editing it, not at the moment they try to save.
      if (session.lossWarnings.length > 0) {
        effects.push({ kind: 'warn-loss', items: session.lossWarnings });
      }
      return { session: { ...session, phase: 'preparing-edit' }, effects };
    }

    case 'working-copy-ready': {
      if (session.phase !== 'preparing-edit') return noop(session);
      return {
        session: { ...session, phase: 'editing', workingCopy: command.ref },
        // §13 step 4: recovery data exists from the first moment of editing,
        // before any edit has been made.
        effects: [{ kind: 'write-recovery-point', revision: session.revision, atMs: command.ref.createdAtMs }],
      };
    }

    case 'working-copy-failed': {
      if (session.phase !== 'preparing-edit') return noop(session);
      return {
        session: { ...session, phase: 'read-only', lastFailure: command.reason },
        effects: [{
          kind: 'notify',
          messageKey: command.reason === 'storage-full'
            ? 'error.notEnoughStorage' : 'error.unableToRead',
        }],
      };
    }

    case 'edit-applied': {
      if (session.phase !== 'editing' && session.phase !== 'save-failed') return noop(session);
      // Editing after a failed save returns to the editing phase; the failure
      // is not sticky, but the retained working copy is.
      return {
        session: { ...session, phase: 'editing', revision: session.revision + 1 },
        effects: [],
      };
    }

    case 'idle-timeout': {
      if (session.phase !== 'editing') return noop(session);
      if (!needsRecoveryPoint(session)) return noop(session);
      return {
        session,
        effects: [
          { kind: 'flush-working-copy', revision: session.revision },
          { kind: 'write-recovery-point', revision: session.revision, atMs: command.atMs },
        ],
      };
    }

    case 'lifecycle-suspend': {
      // §23 requires a save when the app is backgrounded, when a call arrives
      // and before a desktop window closes. All three land here, and all three
      // must flush even if an idle timeout has not elapsed.
      if (session.phase !== 'editing' && session.phase !== 'save-failed') return noop(session);
      if (!needsRecoveryPoint(session)) return noop(session);
      return {
        session,
        effects: [
          { kind: 'flush-working-copy', revision: session.revision },
          { kind: 'write-recovery-point', revision: session.revision, atMs: command.atMs },
        ],
      };
    }

    case 'recovery-point-written': {
      const points = [...session.recoveryPoints, command.point];
      const drop = prunable(points, command.point.createdAtMs, policy);
      const kept = points.filter((p) => !drop.includes(p));
      return {
        session: {
          ...session,
          recoveryPoints: kept,
          recoveredRevision: Math.max(session.recoveredRevision, command.point.revision),
        },
        effects: drop.length > 0 ? [{ kind: 'prune-recovery-points', drop }] : [],
      };
    }

    case 'save-as': {
      if (session.phase !== 'editing' && session.phase !== 'save-failed') return noop(session);
      return {
        session: { ...session, phase: 'saving' },
        effects: [{
          kind: 'write-new-file',
          suggestedName: command.suggestedName,
          revision: session.revision,
        }],
      };
    }

    case 'overwrite-original': {
      if (session.phase !== 'editing' && session.phase !== 'save-failed') return noop(session);
      // §4.10 / §13 step 8: replacing the original always asks first. The
      // reducer cannot be talked into skipping this — there is no command that
      // writes over the original without `overwrite-confirmed`.
      return { session, effects: [{ kind: 'confirm-overwrite' }] };
    }

    case 'overwrite-confirmed': {
      if (session.phase !== 'editing' && session.phase !== 'save-failed') return noop(session);
      return {
        session: { ...session, phase: 'saving' },
        effects: [{ kind: 'write-over-original', revision: session.revision }],
      };
    }

    case 'replace-existing-confirmed': {
      if (session.phase !== 'saving' && session.phase !== 'save-failed') return noop(session);
      return {
        session: { ...session, phase: 'saving', lastFailure: null },
        effects: [{
          kind: 'write-new-file',
          suggestedName: session.original.displayName,
          revision: session.revision,
        }],
      };
    }

    case 'save-succeeded': {
      if (session.phase !== 'saving') return noop(session);
      const effects: Effect[] = [];
      // §13 / §42-27: the file is not "saved" until it has been reopened and
      // its structure checked.
      if (!command.ref.validated) {
        effects.push({ kind: 'validate-export', handle: command.ref.handle });
      }
      return {
        session: {
          ...session,
          phase: 'editing',
          lastSaved: command.ref,
          savedRevision: command.ref.revision,
          saveAttempts: 0,
          lastFailure: null,
        },
        effects,
      };
    }

    case 'save-failed': {
      if (session.phase !== 'saving') return noop(session);
      const attempt = session.saveAttempts + 1;
      const effects: Effect[] = [];

      // §13: the editing copy survives a failed save. This is the single most
      // important line in the module — without it, a full disk destroys work.
      if (session.workingCopy) {
        effects.push({
          kind: 'keep-working-copy',
          ref: session.workingCopy,
          reason: `save failed: ${command.reason}`,
        });
      }

      if (command.reason === 'name-conflict') {
        // Never replace an existing file silently (§13).
        effects.push({ kind: 'confirm-replace-existing', name: session.original.displayName });
      } else {
        effects.push({
          kind: 'notify',
          messageKey: command.reason === 'storage-full' ? 'error.notEnoughStorage'
            : command.reason === 'permission-expired' ? 'error.permissionExpired'
              : command.reason === 'validation-failed' ? 'error.previewFailed'
                : 'error.editsSavedTemporarily',
        });
        const delay = RETRY_DELAYS_MS[attempt - 1];
        if (delay !== undefined && command.reason !== 'cancelled') {
          effects.push({ kind: 'schedule-retry', delayMs: delay, attempt });
        }
      }

      return {
        session: { ...session, phase: 'save-failed', saveAttempts: attempt, lastFailure: command.reason },
        effects,
      };
    }

    case 'retry-save': {
      if (session.phase !== 'save-failed') return noop(session);
      return {
        session: { ...session, phase: 'saving' },
        effects: [{
          kind: 'write-new-file',
          suggestedName: session.original.displayName,
          revision: session.revision,
        }],
      };
    }

    case 'discard-edits': {
      if (session.workingCopy === null) return noop(session);
      return {
        session: {
          ...session,
          phase: 'read-only',
          workingCopy: null,
          revision: session.savedRevision,
        },
        effects: [
          { kind: 'release-working-copy', ref: session.workingCopy },
          { kind: 'notify', messageKey: 'status.originalUnchanged' },
        ],
      };
    }

    case 'close': {
      const effects: Effect[] = [];
      if (session.workingCopy) {
        if (isDirty(session)) {
          // Unsaved work outlives the session so §23 recovery can offer it back.
          effects.push({
            kind: 'keep-working-copy',
            ref: session.workingCopy,
            reason: 'closed with unsaved edits',
          });
        } else {
          effects.push({ kind: 'release-working-copy', ref: session.workingCopy });
        }
      }
      return { session: { ...session, phase: 'closed' }, effects };
    }

    default: {
      // Exhaustiveness: adding a command without handling it fails to compile.
      const never: never = command;
      return noop(session as DocumentSession & typeof never);
    }
  }
}

/** Apply a sequence of commands, collecting every effect in order. */
export function reduceAll(
  session: DocumentSession,
  commands: readonly Command[],
  policy: RecoveryPolicy = DEFAULT_RECOVERY_POLICY,
): Transition {
  let current = session;
  const effects: Effect[] = [];
  for (const c of commands) {
    const step = reduce(current, c, policy);
    current = step.session;
    effects.push(...step.effects);
  }
  return { session: current, effects };
}
